import {
  MessageSquare,
  Clock,
  Users,
  ClipboardCheck,
  Home,
  Trophy,
  Mail,
  Shirt,
  Zap,
  Search,
  Cpu,
  PiggyBank,
  Monitor,
  Heart,
  Award,
  Shield,
  Coins,
  TrendingUp,
  Activity,
  Sparkles,
  Database
} from 'lucide-react';

// Game Data Imports
import { SWIPE_GAMES } from '../../features/pro-module/data/swipeGames';
import { CHAT_GAMES } from '../../features/pro-module/data/chatGames'; 
import { ROAST_GAMES } from '../../features/pro-module/data/roastGames';
import { BATTLE_GAMES } from '../../features/pro-module/data/battleGames'; 
import { EMAIL_GAMES } from '../../features/pro-module/data/emailGames'; 
import { AI_AUDIT_GAMES } from '../../features/pro-module/data/aiAuditGames';
import { NEGOTIATION_GAMES } from '../../features/pro-module/data/negotiationGames';
import { LEASE_GAMES } from '../../features/pro-module/data/leaseGames';
import { INTERVIEW_GAMES } from '../../features/pro-module/data/interviewGames';

/**
 * HIGH-LEVEL CURRICULUM MAP (Updated with NACE Competencies)
 * NACE Standards: Career & Self-Development, Communication, Critical Thinking, 
 * Equity & Inclusion, Leadership, Professionalism, Teamwork, Technology.
 */
export const CURRICULUM_MAP = [
  { 
    id: 'pro-module', 
    title: 'Professionalism 101', 
    category: 'Foundation', 
    nace: 'Professionalism', 
    icon: Zap, 
    color: 'text-yellow-600', 
    bg: 'bg-yellow-50' 
  },
  { 
    id: 'ai-lit', 
    title: 'AI Literacy', 
    category: 'Foundation', 
    nace: 'Technology', 
    icon: Cpu, 
    color: 'text-blue-600', 
    bg: 'bg-blue-50' 
  },
  { 
    id: 'renter-module', 
    title: 'Contractual Ambiguity Audit', 
    category: 'Skills', 
    nace: 'Critical Thinking', 
    icon: Shield, 
    color: 'text-emerald-600', 
    bg: 'bg-emerald-50' 
  },
  { 
    id: 'networking', 
    title: 'Modern Networking', 
    category: 'Social', 
    nace: 'Communication', 
    icon: Users, 
    color: 'text-indigo-600', 
    bg: 'bg-indigo-50' 
  },
  { 
    id: 'interview', 
    title: 'Interview Mastery', 
    category: 'Placement', 
    nace: 'Communication', 
    icon: MessageSquare, 
    color: 'text-pink-600', 
    bg: 'bg-pink-50' 
  },
  { 
    id: 'tech-stack', 
    title: 'Workplace Tech Stack', 
    category: 'Skills', 
    nace: 'Technology', 
    icon: Monitor, 
    color: 'text-slate-600', 
    bg: 'bg-slate-50' 
  },
  { 
    id: 'eq', 
    title: 'Emotional Intelligence', 
    category: 'Skills', 
    nace: 'Teamwork', 
    icon: Heart, 
    color: 'text-red-600', 
    bg: 'bg-red-50' 
  },
  { 
    id: 'leadership', 
    title: 'Leadership Micro', 
    category: 'Skills', 
    nace: 'Leadership', 
    icon: Award, 
    color: 'text-orange-600', 
    bg: 'bg-orange-50' 
  },
  { 
    id: 'time-mgmt', 
    title: 'Focus & Time Management', 
    category: 'Efficiency', 
    nace: 'Professionalism', 
    icon: Clock, 
    color: 'text-cyan-600', 
    bg: 'bg-cyan-50' 
  },
  { 
    id: 'negotiation', 
    title: 'Salary Negotiation', 
    category: 'Placement', 
    nace: 'Critical Thinking', 
    icon: Coins, 
    color: 'text-amber-600', 
    bg: 'bg-amber-50' 
  }
];

/**
 * MODULE 1: MODERN NETWORKING CONTENT
 */
export const networkingModuleContent = [
  {
    id: 'net-part1',
    title: 'Spatial Directory Audit',
    description: 'Bypass gatekeepers and inspect cross-functional organizational node titles to navigate compliance, risk, and technical authority.',
    icon: Users,
    videos: [],
    reinforcement: {
      type: 'spatialAudit',
      content: {
        id: 'game_modern_networking',
        preGameBriefing: {
          title: "Cross-Functional Directory Audit",
          script: "Organizational charts are not linear. To solve complex enterprise issues without creating decision drift, you must locate and engage the true subject matter expert. Hover over masked directory nodes to inspect authority before taking action.",
          videoUrl: null
        },
        failureLogic: {
          'Decision Drift Trap': {
            title: "Decision Drift Triggered",
            script: "You escalated a routine cross-functional issue upward to an executive manager. Punting operational questions to executives clogs decision bandwidth."
          },
          'Siloed Trap': {
            title: "Siloed Echo Chamber",
            script: "You consulted a peer within your own department who lacks horizontal authority over the cross-functional risk. Expand your domain boundaries."
          },
          'Irrelevant': {
            title: "Systemic Misalignment",
            script: "You engaged a department with zero functional jurisdiction over the operational risk. Develop your enterprise organizational literacy."
          }
        }
      }
    }
  }
];

/**
 * MODULE 3: WORKPLACE TECH STACK CONTENT
 */
export const techStackModuleContent = [
  {
    id: 'tech-stack-part1',
    title: 'Enterprise Architecture Navigation',
    description: 'Differentiate between official Systems of Record and dangerous Shadow IT channels.',
    icon: Database,
    videos: [],
    reinforcement: {
      type: 'swipe',
      content: {
          id: 'game_tech_stack',
          preGameBriefing: {
              title: "System of Record Protocol",
              script: "Enterprise scalability relies on data hygiene. 'Systems of Record' are official, auditable databases (e.g., Salesforce, Jira, GitHub). 'Shadow IT' refers to ephemeral, untrackable channels (e.g., Slack DMs, local desktop files). Your Mission: Swipe left for 'System of Record' and right for 'Shadow IT'. Storing corporate data in Shadow IT creates massive legal and operational risk.",
              videoUrl: null
          },
          config: {
              mode: 'horizontal',
              instruction: "System of Record (Left) or Shadow IT (Right)?",
              labels: { 
                  A: { text: "System of Record", icon: Database, color: "bg-blue-100 text-blue-600" }, 
                  B: { text: "Ephemeral / Shadow IT", icon: MessageSquare, color: "bg-red-100 text-red-600" } 
              }
          }
      }
    }
  }
];

/**
 * MODULE 4: EMOTIONAL INTELLIGENCE CONTENT
 */
export const eqModuleContent = [
  {
    id: 'eq-part1',
    title: 'Fragment Synthesis',
    description: 'Construct professional responses to high-stress, blunt critique without triggering a Defensive Escalation Bias.',
    icon: Heart,
    videos: [],
    reinforcement: {
      type: 'chatHero',
      content: {
        id: 'game_eq_builder',
        preGameBriefing: {
          title: "Emotional Regulation & Coachability",
          script: "In the enterprise, feedback is often blunt, hurried, and delivered under pressure. Your natural instinct will be to defend your ego, shift blame, or tone-police your manager. This is called Defensive Escalation Bias. Your Mission: Assemble the optimal response from the fragments provided. Demonstrate Extreme Ownership. Any defensive trap chips will result in immediate failure.",
          videoUrl: null
        }
      }
    }
  }
];

/**
 * MODULE 5: LEADERSHIP MICRO CONTENT
 */
export const leadershipModuleContent = [
  {
    id: 'lead-part1',
    title: 'Executive Problem Solving',
    description: 'Navigate high-stakes scenarios using extreme ownership without passing the cognitive burden upward.',
    icon: Award,
    videos: [],
    reinforcement: {
      type: 'dynamicSJT',
      content: {
        id: 'game_leadership_micro'
      }
    }
  }
];

/**
 * MODULE 6: FOCUS & TIME MANAGEMENT CONTENT
 */
export const timeMgmtModuleContent = [
  {
    id: 'time-part1',
    title: 'Split-Screen Triage',
    description: 'Execute deep-work focus while mitigating aggressive, randomized organizational noise.',
    icon: Clock,
    videos: [],
    reinforcement: {
      type: 'chronosAudit',
      content: {
          id: 'game_chronos_engine',
          preGameBriefing: {
              title: "The Chronos Engine",
              script: "Deep work is the most valuable executive skill. You are about to draft a critical strategic report. Distractions will bombard your feed. You must decide instantly: Ignore, Schedule, or take Immediate Action. Any wrong choice will shatter your flow state and lock your keyboard. Protect your focus.",
              videoUrl: null
          }
      }
    }
  }
];

/**
 * MODULE 7: SALARY NEGOTIATION CONTENT
 */
export const negotiationModuleContent = [
  {
    id: 'neg-part1',
    title: 'The Negotiation Closer',
    description: 'Master the win-win salary conversation without resorting to emotional ultimatums.',
    icon: Coins,
    videos: [
      'Video 1: The Psychology of the First Offer',
      'Video 2: Researching Your Market Value',
      'Video 3: The "Meet in the Middle" Closer'
    ],
    reinforcement: {
      type: 'negotiation',
      content: {
          id: 'game_salary_negotiation',
          preGameBriefing: {
              title: "Adaptive Adversary Protocol",
              script: "Negotiation is a test of emotional regulation and financial literacy. Corporate recruiters will anchor low, use artificial deadlines, and test your boundaries. Your Mission: Maintain professional composure. Maximize your total rewards package (Base, Equity, Bonuses) without issuing toxic ultimatums. Aggressive responses burn bridges and rescind offers.",
              videoUrl: null
          }
      }
    }
  },
  {
    id: 'neg-part2',
    title: 'The Internal Promotion',
    description: 'Pivot the ask when the cash budget is frozen.',
    icon: TrendingUp,
    videos: [],
    reinforcement: {
      type: 'negotiation',
      content: NEGOTIATION_GAMES['internal-promotion']
    }
  },
  {
    id: 'neg-part3',
    title: 'The Exploding Offer',
    description: 'Defuse high-pressure bullying tactics to buy yourself time.',
    icon: Clock,
    videos: [],
    reinforcement: {
      type: 'negotiation',
      content: NEGOTIATION_GAMES['exploding-offer']
    }
  }
];

/**
 * AI LITERACY MODULE CONTENT
 */
export const aiLiteracyModuleContent = [
  {
    id: 'ai-part1',
    title: 'The SIFT Framework',
    description: 'Stop, Investigate, Find, and Trace. Your defense against hallucinations.',
    icon: Search,
    videos: ['Video 1: Intro to SIFT'],
    reinforcement: {
      type: 'aiAudit',
      content: AI_AUDIT_GAMES['nj-market-report']
    }
  },
  {
    id: 'ai-part2',
    title: 'Prompt Engineering',
    description: 'Stop the "Drunk Intern." Learn to direct the AI with Role, Tone, and Context.',
    icon: Sparkles,
    videos: ['Video 2: Garbage In, Garbage Out'],
    reinforcement: {
      type: 'promptGolf',
      content: AI_AUDIT_GAMES['prompt-golf-basic']
    }
  },
  {
    id: 'ai-part3',
    title: 'Shadow AI Protection',
    description: 'Defend enterprise data boundaries. Identify unsanctioned leaks before you click submit.',
    icon: Shield,
    videos: ['Video 3: The Perimeter Breach'],
    reinforcement: {
      type: 'swipe',
      content: SWIPE_GAMES['shadow-ai-defender']
    }
  },
  {
    id: 'ai-part4',
    title: 'Codebase Audit',
    description: 'Intercept LLM-generated code hallucinations before they merge.',
    icon: Cpu,
    videos: [], 
    reinforcement: {
      type: 'aiAudit',
      content: AI_AUDIT_GAMES['codebase-audit']
    }
  },
  {
    id: 'ai-part5',
    title: 'Executive Financial Audit',
    description: 'Catch the mathematical confidence trick.',
    icon: Search,
    videos: [],
    reinforcement: {
      type: 'aiAudit',
      content: AI_AUDIT_GAMES['financial-projection-audit']
    }
  }
];

/**
 * RENTER MODULE (LEASE DEFENDER) CONTENT
 */
export const renterModuleContent = [
  {
    id: 'lease-part1',
    title: 'Renter Rights Audit',
    description: 'Find the illegal clauses before you sign.',
    icon: Shield,
    videos: ['Video 1: Lease Defense 101'],
    reinforcement: {
      type: 'leaseDefender',
      content: LEASE_GAMES['first-apartment-lease']
    }
  },
  {
    id: 'lease-part2',
    title: 'The Corporate "Luxury" Trap',
    description: 'Defeat algorithmic pricing and variable fee structures.',
    icon: Shield,
    videos: [],
    reinforcement: {
      type: 'leaseDefender',
      content: LEASE_GAMES['luxury-building-traps']
    }
  },
  {
    id: 'lease-part3',
    title: 'The Shady Sublet',
    description: 'Navigate the dangers of handshake deals and illegal clauses.',
    icon: Home,
    videos: [],
    reinforcement: {
      type: 'leaseDefender',
      content: LEASE_GAMES['shady-sublet']
    }
  }
];

/**
 * INTERVIEW MASTERY MODULE CONTENT
 */
export const interviewModuleContent = [
  {
    id: 'int-part1',
    title: 'Behavioral High-Stakes',
    description: 'Master the STAR method for placement.',
    icon: MessageSquare,
    videos: [
      'Video 1: The First 30 Seconds',
      'Video 2: The STAR Method',
      'Video 3: The Strength Paradox'
    ],
    reinforcement: {
      type: 'chatHero', 
      content: {
          id: 'game_behavioral_gauntlet',
          preGameBriefing: {
              title: "The Behavioral Gauntlet",
              script: "Interviews are no longer about your resume. Executive hiring managers will interrupt you, stress-test your narrative, and force you into a corner to see how you react. Your Mission: Assemble the optimal pivot response. Never freeze. Never get defensive. Adapt instantly.",
              videoUrl: null
          }
      }
    }
  }
];

/**
 * PROFESSIONALISM 101 CONTENT
 */
export const professionalismModuleContent = [
  {
    id: 'part1',
    title: 'Part 1: The Medium Matters',
    description: 'Slack vs. Email vs. Zoom.',
    icon: Zap,
    videos: ['Video 1: Right Channel, Wrong Channel'], 
    reinforcement: { type: 'swipe', content: SWIPE_GAMES['channel-surfer'] }
  },
  {
    id: 'part2',
    title: 'Part 2: The One-Touch Rule',
    description: 'Stop sending "Hey..." and ghosting.',
    icon: MessageSquare,
    videos: ['Video 1: The "Hey" Problem'],
    reinforcement: { type: 'chatHero', content: CHAT_GAMES['slack-attack'] }
  },
  {
    id: 'part3',
    title: 'Part 3: Email Like a Pro',
    description: 'The BLUF method.',
    icon: Mail,
    videos: ['Video 1: The Wall of Text'],
    reinforcement: { type: 'emailGame', content: EMAIL_GAMES['wall-of-text'] }
  },
  {
    id: 'part4',
    title: 'Part 4: Time & Gatekeeping',
    description: "Protecting your focus (and your boss's).",
    icon: Clock,
    videos: ['Video 1: Urgent vs. Important'],
    reinforcement: { type: 'swipe', content: SWIPE_GAMES['gatekeeper'] }
  },
  {
    id: 'part5',
    title: 'Part 5: Meeting Hero',
    description: 'Saving the call when things go wrong.',
    icon: ClipboardCheck,
    videos: ['Video 1: Zoom Etiquette 101'],
    reinforcement: { type: 'chatHero', content: CHAT_GAMES['game_meeting_hero'] }
  },
  {
    id: 'part6',
    title: 'Part 6: Attention to Detail',
    description: 'Catching errors before you hit send.',
    icon: Search,
    videos: ['Video 1: The "Detail Oriented" Paradox'],
    reinforcement: { type: 'roaster', content: ROAST_GAMES['bad-resume-1'] }
  },
  {
    id: 'part7',
    title: 'Part 7: Decoding Culture',
    description: 'Red Flags vs. Green Flags.',
    icon: Users,
    videos: ['Video 1: The "Family" Trap'],
    reinforcement: { type: 'swipe', content: SWIPE_GAMES['culture-vibe'] }
  },
  {
    id: 'part8',
    title: 'Part 8: The Visual Rules',
    description: 'Dress codes and camera presence.',
    icon: Shirt,
    videos: ['Video 1: The Dress Code Fail'],
    reinforcement: { type: 'swipe', content: SWIPE_GAMES['dress-code'] }
  },
  {
    id: 'part10',
    title: 'Part 9: Expense Roulette',
    description: 'Navigate corporate credit card ethics.',
    icon: PiggyBank,
    videos: [],
    reinforcement: { type: 'swipe', content: SWIPE_GAMES['expense-roulette'] }
  },
  {
    id: 'part11',
    title: 'Part 10: Unwritten Etiquette',
    description: 'Avoid the Career Limiting Moves.',
    icon: Users,
    videos: [],
    reinforcement: { type: 'swipe', content: SWIPE_GAMES['office-etiquette'] }
  },
  {
    id: 'part9',
    title: 'Final Exam: The Gauntlet',
    description: 'Prove you are ready.',
    icon: Trophy,
    videos: [], 
    reinforcement: { type: 'battleRoyale', content: BATTLE_GAMES['final-exam-1'] }
  }
];

// --- EXPORTED INITIAL STATE ---
// Dynamically generates the initial progress object by merging all lesson part IDs.
export const initialProgress = [
  ...professionalismModuleContent,
  ...networkingModuleContent,
  ...techStackModuleContent,
  ...eqModuleContent,
  ...leadershipModuleContent,
  ...timeMgmtModuleContent,
  ...aiLiteracyModuleContent,
  ...negotiationModuleContent,
  ...renterModuleContent,
  ...interviewModuleContent
].reduce((acc, part) => {
  acc[part.id] = false;
  return acc;
}, {});